import gsap from "gsap";
import { ScrambleTextPlugin } from "gsap/ScrambleTextPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrambleTextPlugin, ScrollTrigger);

// 型定義
type AsciiArtOptions = {
  maxWidth?: number;
  invert?: boolean;
  target?: HTMLElement;
  keepImageVisible?: boolean;
  fontSize?: number;
  animationDuration?: number;
  animationDelay?: number;
};

// const ASCII_CHARS = [
//   "窮",
//   "屈",
//   "働",
//   "棹",
//   "流",
//   "通",
//   "情",
//   "意",
//   "角",
//   "智",
//   "住",
//   "世",
//   "地",
//   "人",
//   "立",
//   "だ",
//   "け",
//   "が",
//   "を",
//   "ば",
//   "さ",
//   "せ",
//   "れ",
//   "る",
//   "と",
//   "か",
//   "に",
//   "み",
//   "つ",
//   "お",
//   "ち",
//   "じ",
//   "ょ",
//   "う",
//   "く",
//   "は",
//   "ど",
//   "の",
//   "い",
//   "。",
//   "　",
// ];

const ASCII_CHARS = ["桜", "花", "咲", "の", "が", "く", "。"];

// ============================================================================
// WebGL GPU Accelerated ASCII Art Generator
// ============================================================================

/**
 * Vertex Shader: 画面全体を覆う四角形を描画
 */
const VERTEX_SHADER = `
  attribute vec2 a_position;
  attribute vec2 a_texCoord;
  varying vec2 v_texCoord;

  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_texCoord = a_texCoord;
  }
`;

/**
 * Fragment Shader: 動画テクスチャから輝度を計算
 */
const FRAGMENT_SHADER = `
  precision mediump float;
  varying vec2 v_texCoord;
  uniform sampler2D u_texture;

  void main() {
    vec4 color = texture2D(u_texture, v_texCoord);
    // 輝度計算 (Rec.709)
    float brightness = 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b;
    // 輝度を0-1の範囲で出力 (RGBすべて同じ値にして後でCPU側で読み取りやすくする)
    gl_FragColor = vec4(brightness, brightness, brightness, 1.0);
  }
`;

/**
 * WebGL ASCII Art Generator Class
 * GPU上で動画フレームの輝度計算を行い、CPU側でASCII文字に変換
 */
class WebGLAsciiGenerator {
  private gl: WebGLRenderingContext;
  private program: WebGLProgram;
  private texture: WebGLTexture;
  private framebuffer: WebGLFramebuffer;
  private renderTexture: WebGLTexture;
  private canvas: HTMLCanvasElement;
  private width: number = 0;
  private height: number = 0;

  constructor() {
    // オフスクリーンキャンバスを作成
    this.canvas = document.createElement("canvas");
    const gl = this.canvas.getContext("webgl", {
      preserveDrawingBuffer: true,
      antialias: false,
    });

    if (!gl) {
      throw new Error("WebGL not supported");
    }

    this.gl = gl;

    // シェーダーをコンパイル
    const vertexShader = this.compileShader(gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragmentShader = this.compileShader(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);

    // プログラムをリンク
    const program = gl.createProgram();
    if (!program) {
      throw new Error("Failed to create WebGL program");
    }

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const info = gl.getProgramInfoLog(program);
      throw new Error(`Failed to link program: ${info}`);
    }

    this.program = program;

    // テクスチャを作成
    const texture = gl.createTexture();
    if (!texture) {
      throw new Error("Failed to create texture");
    }
    this.texture = texture;

    // フレームバッファとレンダーテクスチャを作成
    const framebuffer = gl.createFramebuffer();
    const renderTexture = gl.createTexture();
    if (!framebuffer || !renderTexture) {
      throw new Error("Failed to create framebuffer or render texture");
    }
    this.framebuffer = framebuffer;
    this.renderTexture = renderTexture;

    // 四角形のジオメトリをセットアップ
    this.setupGeometry();
  }

  private compileShader(type: number, source: string): WebGLShader {
    const gl = this.gl;
    const shader = gl.createShader(type);
    if (!shader) {
      throw new Error("Failed to create shader");
    }

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const info = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(`Failed to compile shader: ${info}`);
    }

    return shader;
  }

  private setupGeometry(): void {
    const gl = this.gl;
    gl.useProgram(this.program);

    // 画面全体を覆う四角形 (-1 to 1)
    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);

    // テクスチャ座標 (0 to 1) - Y軸を反転して正しい向きに
    const texCoords = new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]);

    // Position attribute
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(this.program, "a_position");
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // TexCoord attribute
    const texCoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);

    const texCoordLocation = gl.getAttribLocation(this.program, "a_texCoord");
    gl.enableVertexAttribArray(texCoordLocation);
    gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);
  }

  /**
   * キャンバスとフレームバッファのサイズを設定
   */
  setSize(width: number, height: number): void {
    if (this.width === width && this.height === height) {
      return;
    }

    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;

    const gl = this.gl;

    // レンダーテクスチャのサイズを設定
    gl.bindTexture(gl.TEXTURE_2D, this.renderTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    // フレームバッファにレンダーテクスチャをアタッチ
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.renderTexture,
      0
    );

    gl.viewport(0, 0, width, height);
  }

  /**
   * 動画フレームをGPUで処理して輝度データを取得
   */
  processFrame(video: HTMLVideoElement): Uint8Array {
    const gl = this.gl;

    // 動画テクスチャを更新
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    // フレームバッファにレンダリング
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
    gl.useProgram(this.program);

    // テクスチャをバインド
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    const textureLocation = gl.getUniformLocation(this.program, "u_texture");
    gl.uniform1i(textureLocation, 0);

    // 描画
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // ピクセルデータを読み取り
    const pixels = new Uint8Array(this.width * this.height * 4);
    gl.readPixels(0, 0, this.width, this.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    return pixels;
  }

  /**
   * リソースを解放
   */
  dispose(): void {
    const gl = this.gl;
    gl.deleteTexture(this.texture);
    gl.deleteTexture(this.renderTexture);
    gl.deleteFramebuffer(this.framebuffer);
    gl.deleteProgram(this.program);
  }
}

// 使用する文字 (半角)
// const ASCII_CHARS = ["0", "1", " "];

// 動画の読み込みを待つ
const waitForVideoReady = (video: HTMLVideoElement) => {
  if (video.readyState >= 3) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve, reject) => {
    const onCanPlay = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error("動画の読み込みに失敗しました"));
    };
    const cleanup = () => {
      video.removeEventListener("canplay", onCanPlay);
      video.removeEventListener("error", onError);
    };

    video.addEventListener("canplay", onCanPlay, { once: true });
    video.addEventListener("error", onError, { once: true });
  });
};

/**
 * GPU処理された輝度データからASCII文字列を生成
 * @param pixels - WebGLから取得した輝度データ (RGBA形式)
 * @param width - 画像の幅
 * @param height - 画像の高さ
 * @param invert - 輝度を反転するか
 */
const generateAsciiFromPixels = (
  pixels: Uint8Array,
  width: number,
  height: number,
  invert: boolean = false
): string => {
  let output = "";
  const charCount = ASCII_CHARS.length - 1;

  for (let y = 0; y < height; y += 1) {
    let row = "";
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      // GPUで計算された輝度値 (R, G, B すべて同じ値)
      const brightness = pixels[offset] / 255;
      const index = invert
        ? Math.floor(brightness * charCount)
        : Math.floor((1 - brightness) * charCount);
      row += ASCII_CHARS[Math.min(charCount, Math.max(0, index))];
    }
    output += row;
    if (y < height - 1) {
      output += "\n";
    }
  }

  return output;
};

/**
 * pre要素をターゲットコンテナにobject-fit: coverと同じ挙動で配置
 * アスペクト比を維持しながらコンテナ全体を覆う
 */
const applyAsciiScale = (element: HTMLElement, target: HTMLElement) => {
  const targetWidth = target.clientWidth;
  const targetHeight = target.clientHeight;
  if (!targetWidth || !targetHeight) return;

  // pre要素の現在のコンテンツの幅と高さを取得
  const contentWidth = element.scrollWidth;
  const contentHeight = element.scrollHeight;
  if (!contentWidth || !contentHeight) return;

  // object-fit: cover と同じ挙動: アスペクト比を維持しながらコンテナを覆う
  const scaleX = targetWidth / contentWidth;
  const scaleY = targetHeight / contentHeight;
  const scale = Math.max(scaleX, scaleY); // coverなので大きい方を採用

  // スケール後のサイズ
  const scaledWidth = contentWidth * scale;
  const scaledHeight = contentHeight * scale;

  // 中央配置のためのオフセット
  const offsetX = (targetWidth - scaledWidth) / 2;
  const offsetY = (targetHeight - scaledHeight) / 2;

  // transformOrigin を左上に設定し、translate と scale を適用
  element.style.transformOrigin = "top left";
  element.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;
};

// setupDragClipping (元のまま)
const setupDragClipping = (asciiWrapper: HTMLElement, container: HTMLElement) => {
  let isDragging = false;

  const onMouseDown = (_e: MouseEvent | TouchEvent) => {
    isDragging = true;
    container.style.userSelect = "none";
    document.body.style.cursor = "ew-resize";
  };

  const onMouseUp = () => {
    if (!isDragging) return;
    isDragging = false;
    container.style.userSelect = "";
    document.body.style.cursor = "";
  };

  const onMouseMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging) return;

    const clientX = (e as MouseEvent).clientX ?? (e as TouchEvent).touches[0].clientX;
    const containerRect = container.getBoundingClientRect();
    const mouseX = clientX - containerRect.left;

    const rightClipPercentage = Math.max(
      0,
      Math.min(100, (1 - mouseX / containerRect.width) * 100)
    );

    asciiWrapper.style.clipPath = `inset(0 ${rightClipPercentage}% 0 0)`;
  };

  container.addEventListener("mousedown", onMouseDown);
  container.addEventListener("touchstart", onMouseDown, { passive: true });

  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("touchmove", onMouseMove);

  document.addEventListener("mouseup", onMouseUp);
  document.addEventListener("touchend", onMouseUp);

  const originalCleanup = (asciiWrapper as any)._cleanup;
  (asciiWrapper as any)._cleanup = () => {
    if (originalCleanup) {
      originalCleanup();
    }
    container.removeEventListener("mousedown", onMouseDown);
    container.removeEventListener("touchstart", onMouseDown);
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("touchmove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    document.removeEventListener("touchend", onMouseUp);
  };
};

/**
 * GPU Accelerated Video to ASCII Converter
 * WebGLを使用して動画フレームの輝度計算をGPU上で実行
 */
export const convertVideoToAscii = async (
  video: HTMLVideoElement,
  options: AsciiArtOptions = {}
) => {
  await waitForVideoReady(video);

  // WebGL ASCII Generator を初期化
  const gpuGenerator = new WebGLAsciiGenerator();

  if (!options.target) {
    console.error("Target element not provided for ASCII art.");
    gpuGenerator.dispose();
    return;
  }

  // 動画の元解像度を取得
  const videoWidth = video.videoWidth || video.width;
  const videoHeight = video.videoHeight || video.height;

  if (!videoWidth || !videoHeight) {
    console.error("Video dimensions not available");
    gpuGenerator.dispose();
    return;
  }

  // ASCII出力の解像度を計算（動画のアスペクト比を維持）
  const maxWidth = options.maxWidth || 120;
  const scale = Math.min(1, maxWidth / videoWidth);
  const asciiWidth = Math.max(1, Math.floor(videoWidth * scale));
  const asciiHeight = Math.max(1, Math.floor(videoHeight * scale));

  // WebGLキャンバスのサイズを設定
  gpuGenerator.setSize(asciiWidth, asciiHeight);

  // --- 1. コンテナと<pre>を1回だけ作成 ---
  const wrapper = document.createElement("div");
  wrapper.style.position = "absolute";
  wrapper.style.inset = "0";
  wrapper.style.clipPath = "inset(0 100% 0 0)"; // 初期非表示
  wrapper.style.backgroundColor = options.invert ? "#fff" : "transparent";
  wrapper.style.display = "flex";
  wrapper.style.alignItems = "flex-start";
  wrapper.style.justifyContent = "flex-start";
  wrapper.style.overflow = "hidden";
  wrapper.dataset.asciiArt = "true";
  wrapper.style.willChange = "clip-path";

  const pre = document.createElement("pre");
  pre.textContent = ""; // 初期テキストは空
  pre.style.margin = "0";
  pre.style.whiteSpace = "pre";
  pre.style.fontFamily = "monospace";
  pre.style.lineHeight = "1";
  pre.style.letterSpacing = "0";
  pre.style.fontSize = `${options.fontSize ?? 8}px`;
  pre.style.color = options.invert ? "#000" : "#fff";
  pre.style.backgroundColor = "transparent";
  pre.style.flex = "1 0 auto";
  pre.style.display = "block";
  pre.style.alignSelf = "flex-start";

  wrapper.appendChild(pre);

  // (targetのposition設定や古いノードの削除)
  const targetStyle = window.getComputedStyle(options.target);
  if (targetStyle.position === "static") {
    options.target.style.position = "relative";
  }
  options.target.querySelectorAll("[data-ascii-art='true']").forEach((node) => {
    if (node instanceof HTMLElement && typeof (node as any)._cleanup === "function") {
      (node as any)._cleanup();
    }
    node.remove();
  });

  // --- 2. GPU Accelerated リアルタイム・アスキーアート・レンダリングループ ---
  let animationFrameId: number | null = null;
  const renderLoop = () => {
    if (video.paused || video.ended || !document.body.contains(wrapper)) {
      animationFrameId = requestAnimationFrame(renderLoop);
      return;
    }

    try {
      // ★ GPU上で輝度計算を実行
      const pixels = gpuGenerator.processFrame(video);

      // ★ GPU計算結果からASCII文字列を生成
      const ascii = generateAsciiFromPixels(
        pixels,
        asciiWidth,
        asciiHeight,
        options.invert || false
      );

      pre.textContent = ascii; // <pre>の内容を毎フレーム更新

      // preの内容更新後にスケーリングを毎回適用
      if (options.target) {
        applyAsciiScale(pre, options.target);
      }
    } catch (error) {
      console.error("ASCII Art generation error in loop:", error);
    }

    animationFrameId = requestAnimationFrame(renderLoop);
  };

  // --- 3. クリーンアップ準備 ---
  const cleanup = () => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    // WebGLリソースを解放
    gpuGenerator.dispose();
  };
  (wrapper as any)._cleanup = cleanup;

  options.target.appendChild(wrapper);

  // --- 4. 最初の1フレームを描画 ---
  try {
    const pixels = gpuGenerator.processFrame(video);
    const firstAscii = generateAsciiFromPixels(
      pixels,
      asciiWidth,
      asciiHeight,
      options.invert || false
    );
    pre.textContent = firstAscii;
    if (options.target) {
      applyAsciiScale(pre, options.target);
    }
  } catch (error) {
    console.error("ASCII Art initial generation error:", error);
  }

  // --- 5. GSAPアニメーションを実行し、完了後にループを開始 ---
  gsap.to(wrapper, {
    clipPath: "inset(0 50% 0 0)", // 左半分表示へ
    opacity: 0.9,
    backgroundColor: "#70B7FF",
    duration: options.animationDuration ?? 1.5,
    delay: options.animationDelay ?? 0.5,
    ease: "power2.out",
    onComplete: () => {
      // アニメーション完了時のスタイルを固定
      wrapper.style.clipPath = "inset(0 50% 0 0)";
      // ドラッグ機能を有効化
      setupDragClipping(wrapper, options.target!);
    },
  });

  renderLoop();
};

// textAnimation (呼び出し側)
export const textAnimation = async () => {
  const container = document.getElementById("container");
  if (!container) return;

  const video = container.querySelector("video");
  if (!(video instanceof HTMLVideoElement)) {
    console.error("Video element not found in container.");
    return;
  }

  try {
    await convertVideoToAscii(video, {
      target: container,
      maxWidth: 180,
      fontSize: 30, // fontSizeはスケーリング前の基準値として機能します
      keepImageVisible: true,
      animationDuration: 3,
      animationDelay: 0.3,
    });
  } catch (error) {
    console.error("[work-088] ASCII変換に失敗しました:", error);
  }
};
